export const SEARCH_FILTER_PARAM_KEYS = {
  page: "page",
  viewAll: "viewAll",
  sort: "sort",
  attributes: "attributes",
  colors: "colors",
  minPrice: "minPrice",
  maxPrice: "maxPrice",
  category: "category",
  search: "search",
};

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickFirstDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
};

export const parseSearchFiltersFromParams = (searchParams) => {
  const pageValue = Number.parseInt(searchParams.get(SEARCH_FILTER_PARAM_KEYS.page), 10);
  const sortValue = Number.parseInt(searchParams.get(SEARCH_FILTER_PARAM_KEYS.sort), 10);

  const minPrice = toNumber(searchParams.get(SEARCH_FILTER_PARAM_KEYS.minPrice));
  const maxPrice = toNumber(searchParams.get(SEARCH_FILTER_PARAM_KEYS.maxPrice));
  const hasValidPriceRange = minPrice !== null && maxPrice !== null;

  return {
    page: Number.isFinite(pageValue) && pageValue > 0 ? pageValue : 1,
    viewAll: searchParams.get(SEARCH_FILTER_PARAM_KEYS.viewAll) === "true",
    sort: Number.isFinite(sortValue) ? sortValue : 1,
    attributes: (searchParams.get(SEARCH_FILTER_PARAM_KEYS.attributes) || "")
      .split(",")
      .filter(Boolean),
    colors: (searchParams.get(SEARCH_FILTER_PARAM_KEYS.colors) || "")
      .split(",")
      .filter(Boolean),
    priceRange: hasValidPriceRange ? [minPrice, maxPrice] : [],
    category: (searchParams.get(SEARCH_FILTER_PARAM_KEYS.category) || "")
      .split(",")
      .filter(Boolean),
    search: searchParams.get(SEARCH_FILTER_PARAM_KEYS.search) || "",
  };
};

export const buildSearchFiltersParams = (filters = {}) => {
  const params = new URLSearchParams();
  const page = Number.parseInt(filters.page, 10);
  const sort = Number.parseInt(filters.sort, 10);
  const category = toArray(filters.category);
  const attributes = toArray(filters.attributes);
  const colors = toArray(filters.colors);
  const search = typeof filters.search === "string" ? filters.search.trim() : "";
  const priceRange = Array.isArray(filters.priceRange) ? filters.priceRange : [];
  const minPrice = toNumber(priceRange[0] ?? filters.minPrice);
  const maxPrice = toNumber(priceRange[1] ?? filters.maxPrice);

  if (category.length > 0) params.set(SEARCH_FILTER_PARAM_KEYS.category, category.join(","));
  if (search) params.set(SEARCH_FILTER_PARAM_KEYS.search, search);
  if (Number.isFinite(page) && page > 1) params.set(SEARCH_FILTER_PARAM_KEYS.page, String(page));
  if (filters.viewAll === true) params.set(SEARCH_FILTER_PARAM_KEYS.viewAll, "true");
  if (Number.isFinite(sort) && sort !== 1) params.set(SEARCH_FILTER_PARAM_KEYS.sort, String(sort));
  if (attributes.length > 0) params.set(SEARCH_FILTER_PARAM_KEYS.attributes, attributes.join(","));
  if (colors.length > 0) params.set(SEARCH_FILTER_PARAM_KEYS.colors, colors.join(","));

  if (minPrice !== null && maxPrice !== null) {
    params.set(SEARCH_FILTER_PARAM_KEYS.minPrice, String(minPrice));
    params.set(SEARCH_FILTER_PARAM_KEYS.maxPrice, String(maxPrice));
  }

  return params;
};

export const buildSearchResultsPath = (filters = {}, basePath = "/search-results") => {
  const params = buildSearchFiltersParams(filters);
  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
};

export const getSearchFiltersFromCms = (cms = {}) => {
  const minPrice = pickFirstDefined(
    cms?.minPrice,
    cms?.min_price,
    cms?.from_price,
    cms?.price_from,
    cms?.filter?.minPrice,
    cms?.filter?.min_price
  );

  const maxPrice = pickFirstDefined(
    cms?.maxPrice,
    cms?.max_price,
    cms?.to_price,
    cms?.price_to,
    cms?.filter?.maxPrice,
    cms?.filter?.max_price
  );

  const category = pickFirstDefined(
    cms?.categoryId,
    cms?.category_id,
    cms?.category,
    cms?.filter?.categoryId,
    cms?.filter?.category_id
  );

  return {
    minPrice,
    maxPrice,
    category,
  };
};
